import type { NavItem } from "@/routes/menuConfig";

/** 不参与权限校验的布局内路径 */
const SKIP_GUARD_PATHS = new Set(["/", "/403"]);

/**
 * 扁平化菜单叶子：href → 所需权限码（无权限码时为 undefined）
 * @param menus 用户侧栏菜单
 */
export const flattenMenuRoutePermissions = (menus: NavItem[]): Map<string, string | undefined> => {
  const map = new Map<string, string | undefined>();
  for (const item of menus) {
    if (item.children?.length) {
      for (const child of item.children) {
        map.set(child.href, child.permission);
      }
      continue;
    }
    map.set(item.href, item.permission);
  }
  return map;
};

/**
 * 判断当前路径是否为菜单中的父级占位路径（无独立页，仅用于重定向）
 * @param pathname 当前路径
 * @param menus 用户侧栏菜单
 */
const isParentMenuPath = (pathname: string, menus: NavItem[]): boolean => {
  return menus.some((item) => item.children?.length && item.href === pathname);
};

/**
 * 判断用户是否可访问当前路由
 * @param pathname 当前路径
 * @param menus 用户侧栏菜单
 * @param permissions 用户权限码列表
 */
export const canAccessRoute = (
  pathname: string,
  menus: NavItem[],
  permissions: string[],
): boolean => {
  if (SKIP_GUARD_PATHS.has(pathname)) return true;
  if (isParentMenuPath(pathname, menus)) return true;

  const routePermissions = flattenMenuRoutePermissions(menus);

  /** 解析菜单项所需权限；无权限码则放行 */
  const allowByPermission = (requiredPermission: string | undefined): boolean => {
    if (!requiredPermission) return true;
    return permissions.includes(requiredPermission);
  };

  // 精确匹配菜单叶子
  if (routePermissions.has(pathname)) {
    return allowByPermission(routePermissions.get(pathname));
  }

  // 详情子路径继承父叶子权限，如 /feedback/123 → /feedback
  let matchedHref: string | undefined;
  for (const href of routePermissions.keys()) {
    if (!pathname.startsWith(`${href}/`)) continue;
    if (!matchedHref || href.length > matchedHref.length) {
      matchedHref = href;
    }
  }
  if (matchedHref) {
    return allowByPermission(routePermissions.get(matchedHref));
  }

  return false;
};
